import React, { useCallback, useEffect, useMemo, useState } from 'react'
import classnames from 'classnames'
import QuantitySelector from '../online_ordering/item_modal/QuantitySelector'
import { debounce } from 'lodash'
import { useCartItemForQuickAdd } from '../../online_ordering/useCartItemForQuickAdd'
import { useAlertModalContext } from 'src/shared/components/common/alert_modal/AlertModal'
import { RankedPromoOfferDiscount } from 'src/apollo/onlineOrdering'
import { REQUEST_FAILURE_MESSAGE } from 'src/shared/js/constants'
import { useOffers } from '../../online_ordering/OffersContext'
import { getOfferPrice } from '../online_ordering/cart/offers/OfferUtils'
import { useCart } from '../../online_ordering/CartContext'
import { reportError } from 'src/lib/js/clientError'
import {
  EditSource,
  ItemSource,
  wrappedModifier
} from '../online_ordering/cart/cartUtils'
import { AddIcon, DoneIcon } from '@toasttab/buffet-pui-icons'
import { useAddToCartHandler } from '../online_ordering/cart/useAddToCartHandler'
import { MenuItem } from './MenuSection'
import { LoadingSpinner } from 'shared/components/common/loading_spinner/LoadingSpinner'
import { AlertModalSource } from 'shared/components/common/alert_modal/AlertModal'
import { t } from 'src/translations/src'
import { useA11y } from '../../online_ordering/useA11y'
import {
  roundPrice,
  useFormatPriceCallback
} from 'src/lib/js/hooks/useFormatPrice'

type QuickAddToCartProps = {
  item: MenuItem
  channelGuid: string | undefined
  itemSource: ItemSource
  showSuccessIcon: boolean
  onItemSuccessfullyAdded: () => void
  openItemModal: (e: React.MouseEvent) => void
  variableMenuSpacing?: boolean
}

const SimpleQuantityDisplayButton = ({
  item,
  handleQuantityClick
}: {
  item: MenuItem
  handleQuantityClick: (e: React.MouseEvent) => void
}) => {
  const { totalQuantity } = useCartItemForQuickAdd(item.guid)

  return (
    <button
      className='quickAddIcons quantityButton'
      data-testid='quick-add-quantity-display-button'
      onClick={(e) => handleQuantityClick(e)}
      aria-label={`${t('add-to-cart-section.quantity')} ${totalQuantity}`}
    >
      {totalQuantity}
    </button>
  )
}

export const QuickAddToCart = ({
  item,
  channelGuid,
  itemSource,
  showSuccessIcon,
  onItemSuccessfullyAdded,
  openItemModal,
  variableMenuSpacing
}: QuickAddToCartProps) => {
  const { itemOffers } = useOffers()
  const { selection, totalQuantity } = useCartItemForQuickAdd(item.guid)
  const { editCartItem, deleteFromCart, applyPromoCode } = useCart()
  const { openAlertModal } = useAlertModalContext()
  const { announce } = useA11y()
  const formatPrice = useFormatPriceCallback()

  const isEligibleForQuickAdd =
    !item.hasModifiers &&
    item.prices?.length === 1 &&
    !item.usesFractionalQuantity
  const [isUpdatingCart, setIsUpdatingCart] = useState(false)
  const [pendingQuantity, setPendingQuantity] = useState<number | null>(null)
  const displayQuantity = pendingQuantity ?? totalQuantity

  // Collapsing animation state
  const [isCollapsing, setIsCollapsing] = useState(false)

  // Calculate offer and price for quick add
  const itemOfferArray = item.guid ? itemOffers[item.guid] : undefined
  const offer = itemOfferArray?.[0] as RankedPromoOfferDiscount | undefined
  const itemBasePrice = item.prices?.[0] || item.price || 0

  const priceAfterOffer = useMemo(() => {
    return getOfferPrice(offer, itemBasePrice)
  }, [offer, itemBasePrice])

  const wrappedMod = useMemo(
    () => wrappedModifier(item),
    [
      item.guid,
      item.itemGroupGuid,
      item.price,
      item.prices,
      item.masterId,
      item.usesFractionalQuantity,
      item.unitOfMeasure,
      item.name
    ]
  )

  const handleAddToCart = useAddToCartHandler()

  const handleCartError = useCallback(
    (error: any) => {
      openAlertModal(
        REQUEST_FAILURE_MESSAGE,
        undefined,
        AlertModalSource.QUICK_ADD_TO_CART
      )
      reportError('Error adding item to cart from quick add.', error)
    },
    [openAlertModal]
  )

  const updateCartQuantity = useCallback(
    async (newQuantity: number) => {
      if (!wrappedModifier || !selection?.guid) return

      try {
        if (newQuantity === 0) {
          await deleteFromCart(selection.guid)
          announce(
            t('cart-item.removed-from-cart', {
              itemName: selection.name || t('cart-item.item')
            })
          )
        } else if (newQuantity > 1) {
          await editCartItem(
            wrappedMod,
            newQuantity,
            selection.guid,
            itemSource,
            EditSource.QuickAddToCart
          )
          announce(
            t('cart-item.quantity-updated', {
              itemName: selection.name || t('cart-item.item'),
              quantity: newQuantity,
              price: formatPrice(roundPrice(itemBasePrice * newQuantity))
            })
          )
        }
      } catch (error) {
        // Only clear on error to revert to actual cart quantity
        setPendingQuantity(null)
        openAlertModal(
          REQUEST_FAILURE_MESSAGE,
          undefined,
          AlertModalSource.QUICK_ADD_TO_CART
        )
        reportError('Error updating cart item from quick add.', error)
      }
    },
    [
      selection?.guid,
      selection?.name,
      deleteFromCart,
      editCartItem,
      wrappedMod,
      itemSource,
      itemBasePrice,
      formatPrice,
      openAlertModal,
      announce
    ]
  )

  const debouncedUpdateCart = useMemo(
    () => debounce(updateCartQuantity, 1500),
    [updateCartQuantity]
  )

  useEffect(() => {
    return () => {
      debouncedUpdateCart.cancel()
    }
  }, [debouncedUpdateCart])

  const handleQuantityChange = useCallback(
    (newQuantity: number, _change: number) => {
      if (newQuantity === 0) {
        setIsCollapsing(true)
        setTimeout(() => {
          setPendingQuantity(newQuantity)
          debouncedUpdateCart(newQuantity)
          setIsCollapsing(false)
        }, 300)
      } else {
        setPendingQuantity(newQuantity)
        debouncedUpdateCart(newQuantity)
      }
    },
    [debouncedUpdateCart]
  )

  const handleQuickAddClick = useCallback(
    async (e: React.MouseEvent) => {
      if (!wrappedMod) {
        return
      }

      if (!isEligibleForQuickAdd) {
        openItemModal(e)
        return
      }

      setIsUpdatingCart(true)

      const additionalTrackData = {
        addToCartSource: 'quick_add_button',
        priceAfterOffer
      }

      const addToCartResponse = await handleAddToCart(
        wrappedMod,
        1,
        channelGuid || '',
        itemSource,
        handleCartError,
        onItemSuccessfullyAdded,
        additionalTrackData
      )

      if (addToCartResponse.errorType !== 'CartModificationError') {
        announce(
          t('cart-item.added', {
            itemName: wrappedMod.name || t('cart-item.item')
          })
        )

        // If this item is being added from a BOGO, add the promo code first
        if (offer?.promoCode) {
          applyPromoCode(offer.promoCode)
        }
      }

      setIsUpdatingCart(false)
    },
    [
      wrappedMod,
      isEligibleForQuickAdd,
      openItemModal,
      channelGuid,
      itemSource,
      applyPromoCode,
      priceAfterOffer,
      offer,
      handleAddToCart,
      handleCartError,
      onItemSuccessfullyAdded,
      announce
    ]
  )

  // Clear pending quantity when cart updates to match it
  useEffect(() => {
    if (pendingQuantity !== null && totalQuantity === pendingQuantity) {
      setPendingQuantity(null)
    }
  }, [totalQuantity, pendingQuantity])

  return (
    <div
      className={classnames('quickAddWrapper', {
        centered: variableMenuSpacing
      })}
    >
      {showSuccessIcon ? (
        <div className='quickAddIcons successIcon'>
          <DoneIcon accessibility='decorative' data-testid='success-icon' />
        </div>
      ) : (displayQuantity && displayQuantity > 0) || isCollapsing ? (
        <>
          {isEligibleForQuickAdd ? (
            <div
              className='quickAddQuantitySelectorWrapper'
              data-testid='quick-add-quantity-selector'
            >
              <QuantitySelector
                min={0}
                canIncrease={true}
                className={classnames('quickAddQuantitySelector', {
                  collapsing: isCollapsing
                })}
                defaultValue={displayQuantity || 1}
                onQuantityChange={handleQuantityChange}
                allowDelete={true}
                onDelete={() => handleQuantityChange(0, 0)}
                usesFractionalQuantity={!!item.usesFractionalQuantity}
                isQuickAdd={true}
                disableAnnouncement={true} // Prevent too many screen reader announcements as we are announcing upon add/quantity update
              />
            </div>
          ) : (
            <SimpleQuantityDisplayButton
              item={item}
              handleQuantityClick={(e: React.MouseEvent) => openItemModal(e)}
            />
          )}
        </>
      ) : (
        <button
          className={classnames('quickAddIcons', {
            addButton: !isUpdatingCart
          })}
          onClick={handleQuickAddClick}
          data-testid='quick-add-button'
          aria-label={t('add-to-cart-section.add-to-cart-aria-label', {
            itemName: item.name
          })}
        >
          {isUpdatingCart ? (
            <LoadingSpinner color='#252525' strokeWidth='4' size='16px' />
          ) : (
            <AddIcon className='addButton' accessibility='decorative' />
          )}
        </button>
      )}
    </div>
  )
}

export default QuickAddToCart
